import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Source} from "react-map-gl/mapbox";
import {useOperator} from "../../../context/OperatorContext";
import Spinner from "../../Spinner";
import Map, {VisitorDensityLayer} from "../../Map";
import {useQuery} from "react-query";
import ngage from "../../../services/ngage";
import {DateTime} from "luxon";
import {useAuth} from "../../../context/AuthContext";

// Types ----

type VisitorDensityMapProps = {
  height: string
  since?: DateTime
  until?: DateTime
  siteId?: number | null
  showSiteSelector?: boolean
}

// Component ----

function VisitorsDensityMap({height, since, until, siteId, showSiteSelector}: VisitorDensityMapProps) {
  const {settings} = useOperator();
  const {can} = useAuth();

  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(siteId ?? null);

  // Fetch site data
  const {data: site, isLoading: siteLoading} = useQuery(
    ['site', {id: selectedSiteId}],
    () => ngage.sites.get(Number(selectedSiteId)),
    {enabled: can('sites:read') && !!selectedSiteId}
  )

  const sinceInTimezone = useMemo(() => site ? since?.setZone(site.timezone, { keepLocalTime: true}) : undefined, [site, since]);
  const untilInTimezone = useMemo(() => site ? until?.setZone(site.timezone, { keepLocalTime: true}) : undefined, [site, until]);

  // Fetch activity heatmap data
  const {data: mapData, isLoading: mapDataLoading} = useQuery(
    ['maps.activity', {since: sinceInTimezone, until: untilInTimezone, siteId: selectedSiteId}],
    ({signal}) => ngage.maps.activity({since: sinceInTimezone, until: untilInTimezone, siteId: selectedSiteId}, {signal}),
    { enabled: !!selectedSiteId && !!sinceInTimezone && !!untilInTimezone}
  );

  // Update the selected site ID when the siteId prop changes
  useEffect(() => {
    if (!siteId) return;
    setSelectedSiteId(siteId);
  }, [siteId]);

  const handleSiteSelect = useCallback((siteId: number | null) => setSelectedSiteId(siteId), []);

  return (
    <Map
      height={height}
      defaultMapStyle={settings?.audience_app.map_style ?? null}
      initialViewState={settings?.audience_app.map_state ?? null}
      hidePointsOfInterest
      showSiteSelector={showSiteSelector}
      onSelectSite={handleSiteSelect}
      siteId={selectedSiteId}
    >
      {/* Data Sources */}
      {!!mapData &&
        <Source id="visitors-density-source" type="geojson" data={mapData}>
          <VisitorDensityLayer id="visitors-density" />
        </Source>
      }

      {/* Loading Spinner */}
      {(mapDataLoading || siteLoading) &&
        <div className="absolute top-0 left-0 h-full w-full flex items-center justify-center pointer-events-none">
          <Spinner color={Spinner.colors.DARK} size={Spinner.sizes.MEDIUM}/>
        </div>
      }
    </Map>
  );
}



// Export ----

export default VisitorsDensityMap;